'use strict'

//* ---------------------------------------
//  Vendors

import 'details-element-polyfill'
import 'focus-visible'
import 'focus-within-polyfill'
import 'objectFitPolyfill'
import 'whatwg-fetch'
import { Modal } from 'bootstrap'
import lottie from 'lottie-web' // eslint-disable-line no-unused-vars

//* ---------------------------------------
//  Components

import Accordion from './components/accordion'
import Announcement from './components/announcement'
import EventCard from './components/eventCard'
import JobForm from './components/jobForm'
import EmbedVideo from './components/embedVideo'
import FaqMulti from './components/faqMulti'
import FormValidate from './components/formValidate'
import HubspotForm from './components/hubspotForm'
import JobCardsFixedCta from './components/jobCardsFixedCta'
import Kanga from './components/kanga'
import KangaScroll from './components/kangaScroll'
import Navbar from './components/navbar'
import MarketoForms from './components/marketoForms'
import PricingToggle from './components/pricingToggle'
import ShareLink from './components/shareLink'
import SignatureCopy from './components/signatureCopy'
import SliderCards from './components/sliderCards'
import SliderContent from './components/sliderContent'
import Tabs from './components/tabs'
import TeamModal from './components/teamModal'
import VideoModal from './components/videoModal'
import VideoScroll from './components/videoScroll'
import AssessmentLibrary from './components/assessmentLibrary'
import UtmCookies from './components/utmCookies'

//* ---------------------------------------
//  Utils

import { setCssVar } from './utils/css'
import {
    createEvent,
    getScrollbarWidth,
    scrollTo,
    setSiblingsAriaHidden
} from './utils/dom'

//* ---------------------------------------
//  Feature Detection

const isTouch = ('ontouchstart' in window
        && !window.navigator.userAgent.match(/X11/))
    || ('DocumentTouch' in window
        && document instanceof window.DocumentTouch)
    || (window.navigator.maxTouchPoints || 0) > 0

document.body.classList
    .add(isTouch
        ? 'touch'
        : 'no-touch')

// Scrollbar width, Overlay is 0
setCssVar('--scrollbar-width',
    `${getScrollbarWidth() || 0}px`)

//* ---------------------------------------
//  Render

/* Render components */
const components = [
    Accordion,
    Announcement,
    AssessmentLibrary,
    FaqMulti,
    FormValidate,
    JobCardsFixedCta,
    EventCard,
    JobForm,
    EmbedVideo,
    HubspotForm,
    Kanga,
    KangaScroll,
    Navbar,
    MarketoForms,
    PricingToggle,
    ShareLink,
    SignatureCopy,
    SliderCards,
    SliderContent,
    Tabs,
    TeamModal,
    VideoModal,
    VideoScroll,
    UtmCookies
]

Promise
    .resolve(components
        .forEach(Component => (new Component())))
    .then(() => {
        window.objectFitPolyfill()
    })

//* ---------------------------------------
//  Global listeners

let resizeDebounce

window
    .addEventListener('resize', () => {
        const scrollY = window.scrollY || window.pageYOffset

        if (document.documentElement.getAttribute('data-scrolly-restore'))
            document.documentElement
                .setAttribute('data-scrolly-restore', scrollY)

        const attr = 'data-resizing'

        if (!document.documentElement.getAttribute(attr))
            document.documentElement.setAttribute(attr, true)

        if (resizeDebounce)
            clearTimeout(resizeDebounce)

        resizeDebounce = setTimeout(() => {
            document.documentElement.removeAttribute(attr)
        }, 200)

        window.objectFitPolyfill()
    })

window
    .addEventListener('scroll:enable', (e) => {
        document.documentElement.classList
            .remove('overflow-hidden')

        document.body.style.top = ''

        const scrollY = Number(document.documentElement
            .getAttribute('data-scrolly-restore') || '0') || 0

        document.documentElement
            .removeAttribute('data-scrolly-restore')

        if (scrollY)
            scrollTo([0, scrollY], {
                speed: 0
            })
    })

window
    .addEventListener('scroll:disable', (e) => {
        if (document.documentElement.classList.contains('overflow-hidden'))
            return

        const scrollY = window.scrollY || window.pageYOffset

        document.documentElement
            .setAttribute('data-scrolly-restore', scrollY)

        document.documentElement.classList
            .add('overflow-hidden')

        document.body.style.top = `-${scrollY}px`
    })

window
    .addEventListener('show.bs.modal', (e) => {
        if (document.documentElement.classList.contains('overflow-hidden'))
            return

        const event = createEvent('scroll:disable', {
            bubbles: false,
            cancelable: true,
            detail: {
                dispatcher: e.target
            }
        })

        window.dispatchEvent(event)

        setSiblingsAriaHidden(e.target, true)
    })

// iOS workaround
window
    .addEventListener('shown.bs.modal', (e) => {
        const backdrop = document.querySelector('.modal-backdrop')

        if (!backdrop)
            return

        backdrop.ontouchmove = (e) => {
            if (!document.documentElement.classList.contains('overflow-hidden'))
                return

            e.preventDefault()
            e.stopPropagation()
        }
    })

window
    .addEventListener('hide.bs.modal', (e) => {
        if (!document.documentElement.classList.contains('overflow-hidden'))
            return

        const event = createEvent('scroll:enable', {
            bubbles: false,
            cancelable: true,
            detail: {
                dispatcher: e.target
            }
        })

        window.dispatchEvent(event)

        setSiblingsAriaHidden(e.target, false)
    })

// Open modal on load when URL has modal hash
const hashModal = window.location.hash

if (hashModal) {
    const modalEl = document.querySelector(hashModal)

    if (modalEl
        && modalEl.classList.contains('modal')
    ) {
        const modal = new Modal(modalEl)
        modal.show()
    }
}

// Modal toggle links
const modalToggles = document.querySelectorAll('[href*="#modal-"]')
modalToggles.forEach((link) => {
    link.addEventListener('click', (e) => {
        const modalId = link.getAttribute('href').split('#')[1]
        const modalEl = document.getElementById(modalId)

        if (modalEl) {
            e.preventDefault()
            const modal = new Modal(modalEl)
            modal.show()
        }
    })
})

//* ---------------------------------------
//  Hot Module Replacement

if (import.meta.hot)
    import.meta.hot.accept()
